import {contractSystemEntity} from "./systemEntityValueByKeyword";
import _ from "lodash";
import {ContractNumberIncrementType} from "./ContractNumberIncrementType";

export type SystemEntity = {
    type: SystemEntityType,
    keyword: string,
    id: string,
    editable: boolean,
    hidden: boolean,
    userPath: string,
    value?: string | boolean,
    raw_value?: string,
    postfix?: string,
    isManualDate?: boolean,
    validationRule?: string,
    computed?: boolean,
    selectedValues?: string[],
    allowedValues?: string[],
    onlyFromCamera?: boolean,
    incrementType?: ContractNumberIncrementType,
    overrideIncrement?: boolean
}

export enum SystemEntityType {
    List = "Список",
    Text = "Текст",
    Date = "Дата",
    Photo = "Фото",
    Formula = "Формула",
    Signature = "Подпись"
}

export enum PassportType {
    russianPassport = "Паспорт гражданина РФ",
    russianInternationalPassport = "Загран. паспорт",
    belarusianPassport = "Паспорт гражданина Республики Беларусь",
    foreignCountryPassport = "Другой документ",
    driverLicense = "Водительское удостоверение"
}

export const LEGACY_PASSPORT_TYPE_ALIASES: Record<string, string> = {
    "Паспорт другого государства": PassportType.foreignCountryPassport,
}

export function normalizePassportType(value?: string | null): string {
    if (!value) {
        return value ?? ""
    }
    const trimmed = value.trim()
    return LEGACY_PASSPORT_TYPE_ALIASES[trimmed] ?? trimmed
}

export function migratePassportTypeListValues(values?: string[] | null): string[] {
    if (!values?.length) {
        return values ?? []
    }
    const result: string[] = []
    const seen = new Set<string>()
    for (const item of values) {
        const normalized = normalizePassportType(item)
        if (!seen.has(normalized)) {
            result.push(normalized)
            seen.add(normalized)
        }
    }
    return result
}

export function passportTypeIncludes(values: string[] | null | undefined, passportType: string): boolean {
    return migratePassportTypeListValues(values).includes(normalizePassportType(passportType))
}

export function patchClientPassportTypeEntity<T extends SystemEntity>(entity: T): T {
    const patched = {...entity}
    if (patched.allowedValues) {
        patched.allowedValues = migratePassportTypeListValues(patched.allowedValues)
    }
    if (patched.selectedValues) {
        patched.selectedValues = migratePassportTypeListValues(patched.selectedValues)
    }
    if (patched.value) {
        patched.value = normalizePassportType(String(patched.value))
    }
    return patched
}

export const allPassportTypes: PassportType[] = [
    PassportType.russianPassport,
    PassportType.russianInternationalPassport,
    PassportType.belarusianPassport,
    PassportType.foreignCountryPassport,
    PassportType.driverLicense
]

export function isDriverLicensePassportType(passportType?: string | null): boolean {
    return normalizePassportType(passportType) === PassportType.driverLicense
}

export function stampDocumentNumberLabel(passportType?: string | null): string {
    const normalized = normalizePassportType(passportType)
    if (normalized === PassportType.driverLicense) {
        return "Водительское удостоверение"
    }
    if (normalized === PassportType.foreignCountryPassport) {
        return "Документ"
    }
    return "Паспорт"
}

export function stampDocumentIssuedAtLabel(passportType?: string | null): string {
    const normalized = normalizePassportType(passportType)
    if (normalized === PassportType.driverLicense || normalized === PassportType.foreignCountryPassport) {
        return "Дата выдачи"
    }
    return "Выдан"
}

/** Кем выдан / код подразделения в штампе — только внутренние РФ и РБ. */
export function supportsStampPassportExtendedFields(passportType?: string | null): boolean {
    const normalized = normalizePassportType(passportType) || PassportType.russianPassport
    return normalized === PassportType.russianPassport || normalized === PassportType.belarusianPassport
}

/**
 * Адрес и фото прописки: РФ, РБ и «Другой документ» (иностранный).
 * Загранпаспорт и ВУ — без прописки.
 */
export function supportsPassportRegistrationFields(passportType?: string | null): boolean {
    const normalized = normalizePassportType(passportType) || PassportType.russianPassport
    return (
        normalized === PassportType.russianPassport ||
        normalized === PassportType.belarusianPassport ||
        normalized === PassportType.foreignCountryPassport
    )
}

export const PASSPORT_TYPES_WITH_REGISTRATION: PassportType[] = [
    PassportType.russianPassport,
    PassportType.belarusianPassport,
    PassportType.foreignCountryPassport,
]

export function appropriatePassportSectionTitle(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType)
        ? "Данные водительского удостоверения:"
        : "Паспортные данные:"
}

export function appropriatePassportPhotosTitle(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType)
        ? "Фотографии водительского удостоверения"
        : "Фотографии паспорта"
}

export function appropriatePassportPhotosSubtitle(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType)
        ? "Для подписания этого документа необходимо приложить фотографии водительского удостоверения."
        : "Для подписания этого документа необходимо приложить фотографии паспорта."
}

export function appropriatePassportPlaceholderForNumber(passportType?: string | null): string {
    if (isDriverLicensePassportType(passportType)) {
        return "Номер водительского удостоверения"
    }

    return "Серия и номер паспорта"
}

export function appropriatePassportPlaceholderForIssuedAt(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType)
        ? "Дата выдачи водительского удостоверения"
        : "Дата выдачи паспорта"
}

export function appropriatePassportPhotoTitle(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType) ? "Фото водительского удостоверения" : "Фото паспорта"
}

export function appropriatePassportSelfieTitle(passportType?: string | null): string {
    return isDriverLicensePassportType(passportType) ? "Селфи с водительским удостоверением" : "Селфи с паспортом"
}

export function getSystemEntityById(id: string): SystemEntity | null {
    for (let entity of allSystemEntities) {
        if (entity.id == id) {
            return entity
        }
    }
    return null
}

export function getSystemEntityByKeyword(id: string): SystemEntity | null {
    for (let entity of allSystemEntities) {
        if (entity.id == id) {
            return entity
        }
    }
    return null
}

export let clientRole: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Тип аккаунта",
    id: "client_role",
    editable: true,
    hidden: true,
    userPath: "role"
}

export let storeClientData: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Хранение персональных данных",
    id: "store_client_data",
    editable: true,
    hidden: true,
    userPath: "store_client_data",
    value: true
}

// FIO
export let clientLastName: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Фамилия клиента",
    id: "client_last_name",
    editable: true,
    hidden: true,
    userPath: "last_name",
    validationRule: "lastName"
}

export let clientFIO: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "ФИО клиента",
    id: "client_fio",
    editable: false,
    hidden: true,
    userPath: "",
    validationRule: "",
    computed: true
}

export let firstSigner: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Исполнитель",
    id: "first_signer",
    editable: false,
    hidden: true,
    userPath: "",
    validationRule: "",
    computed: true
}

// Поле для шапки договора от имени исполнителя (юр. лица/ИП):
// "..., в лице директора ..., действующего на основании Устава"
export let firstSignerRepresentativeBasis: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Основание полномочий исполнителя",
    id: "first_signer_representative_basis",
    editable: true,
    hidden: true,
    userPath: "representative_basis",
}

export let targetUser: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Клиент",
    id: "target_user",
    editable: false,
    hidden: true,
    userPath: "",
    validationRule: "",
    computed: true
}

export let clientFirstName: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Имя клиента",
    id: "client_first_name",
    editable: true,
    hidden: true,
    userPath: "first_name",
    validationRule: "firstName"
}

export let clientMiddleName: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Отчество клиента",
    id: "client_middle_name",
    editable: true,
    hidden: true,
    userPath: "middle_name",
}

export let clientBirthDate: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Дата рождения клиента",
    id: "client_birth_date",
    editable: true,
    hidden: true,
    userPath: "birth_date",
    validationRule: "birthDate"
}

// Contacts

export let clientEmail: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "E-Mail клиента",
    id: "client_email",
    editable: true,
    hidden: false,
    userPath: "email",
    validationRule: "email"
}

export let clientPhoneNumber: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Телефон клиента",
    id: "client_phone_number",
    editable: true,
    hidden: false,
    userPath: "phone_number",
    validationRule: "phoneNumber"
}

// Passport

export let clientPassportType: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Тип документа",
    id: "client_passport_type",
    editable: true,
    hidden: false,
    userPath: "passport.passport_type",
    validationRule: "notEmpty",
    selectedValues: [],
    allowedValues: allPassportTypes,
}

export let clientPassportNumber: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Паспорт клиента",
    id: "client_passport_number",
    editable: true,
    hidden: false,
    userPath: "passport.number",
    validationRule: "" // ruPassportNumber or anotherPassportNumber
}

export let clientPassportIssuedByCode: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Код подразделения",
    id: "client_passport_issued_by_code",
    editable: true,
    hidden: true,
    userPath: "passport.issued_by_code",
    validationRule: "ruPassportIssuedByCode"
}

export let clientPassportIssuedAt: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Дата выдачи паспорта клиента",
    id: "client_passport_issued_at",
    editable: true,
    hidden: true,
    userPath: "passport.issued_at",
    validationRule: "ruPassportIssuedDidAt"
}

export let clientPassportIssuedBy: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Кем выдан паспорт клиента",
    id: "client_passport_issued_by",
    editable: true,
    hidden: true,
    userPath: "passport.issued_by",
    validationRule: "notEmpty"
}

export let clientPassportRegistration: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Адрес прописки клиента",
    id: "client_passport_registration",
    editable: true,
    hidden: true,
    userPath: "passport.registration",
    validationRule: "notEmpty"
}

export let clientSignature: SystemEntity = {
    type: SystemEntityType.Signature,
    keyword: "Рукописная подпись клиента",
    id: "client_signature",
    editable: true,
    hidden: true,
    userPath: "passport.signature",
    validationRule: "notEmpty"
}

export let clientPassportPhoto: SystemEntity = {
    type: SystemEntityType.Photo,
    keyword: "Фото паспорта",
    id: "client_passport_photo",
    editable: true,
    hidden: true,
    userPath: "passport.photo",
    validationRule: "notEmpty",
    onlyFromCamera: false
}

export let clientPassportSelfiePhoto: SystemEntity = {
    type: SystemEntityType.Photo,
    keyword: "Селфи с паспортом",
    id: "client_passport_selfie_photo",
    editable: true,
    hidden: true,
    userPath: "passport.selfie_photo",
    validationRule: "notEmpty",
    onlyFromCamera: false
}

export let clientPassportRegistrationPhoto: SystemEntity = {
    type: SystemEntityType.Photo,
    keyword: "Фото прописки паспорта",
    id: "client_passport_registration_photo",
    editable: true,
    hidden: true,
    userPath: "passport.registration_photo",
    validationRule: "notEmpty",
    onlyFromCamera: false
}

// Entrepreneur

export let clientLawName: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Название организации клиента",
    id: "client_law_name",
    editable: true,
    hidden: false,
    userPath: "law_name",
    validationRule: "lawName"
}

export let clientLawAddress: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Адрес регистрации организации клиента",
    id: "client_law_address",
    editable: true,
    hidden: false,
    userPath: "law_address",
    validationRule: "lawAddress"
}

export let clientTaxId: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "ИНН клиента",
    id: "client_tax_id",
    editable: true,
    hidden: false,
    userPath: "tax_id",
    validationRule: "ruTaxId"
}

export let clientOGRN: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "ОГРН клиента",
    id: "client_ogrn",
    editable: true,
    hidden: false,
    userPath: "ogrn",
    validationRule: "ruOgrn"
}

// Bank

export let clientBankName: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Название банка клиента",
    id: "client_bank_name",
    editable: true,
    hidden: false,
    userPath: "bank_name",
    validationRule: "bankName"
}

export let clientBankAccount: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Расчетный счет банка клиента",
    id: "client_bank_account",
    editable: true,
    hidden: false,
    userPath: "bank_account",
    validationRule: "ruBankAccount"
}

export let clientCorrAccount: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Корр. счет банка клиента",
    id: "client_corr_account",
    editable: true,
    hidden: false,
    userPath: "corr_account",
    validationRule: "ruBankCorrAccount"
}

export let clientBIC: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "БИК банка клиента",
    id: "client_bic",
    editable: true,
    hidden: false,
    userPath: "bic",
    validationRule: "bic"
}

export let contractNumber: SystemEntity = {
    type: SystemEntityType.Text,
    keyword: "Номер договора",
    id: "contract_number",
    editable: true,
    hidden: false,
    userPath: "",
    incrementType: ContractNumberIncrementType.ACCOUNT_WIDE,
    overrideIncrement: false
}
export let currentDate: SystemEntity = {
    type: SystemEntityType.Date,
    keyword: "Текущая дата",
    id: "contract_date",
    editable: true,
    hidden: false,
    userPath: "",
    isManualDate: false
}


export const accountRelatedEntities: SystemEntity[] = [
    clientRole, storeClientData
]

export const contractRelatedEntities: SystemEntity[] = [
    contractNumber, currentDate
]

// CUSTOMER

export const contactsEntities: SystemEntity[] = [
    clientEmail, clientPhoneNumber,
]

export const customerPersonalEntities: SystemEntity[] = [
    clientFIO, clientFirstName, clientLastName, clientMiddleName
]

export const customerBirthDateEntities: SystemEntity[] = [
    clientBirthDate
]

export const passportEntities = [
    clientPassportType,

    clientPassportNumber,
    clientPassportIssuedByCode,
    clientPassportIssuedBy,
    clientPassportIssuedAt,
    clientPassportRegistration,

    clientSignature,

    clientPassportPhoto,
    clientPassportSelfiePhoto,
    clientPassportRegistrationPhoto,
]

export const customerAllEntities: SystemEntity[] = customerPersonalEntities
    .concat(customerBirthDateEntities)
    .concat(passportEntities)

export function isCustomerEntity(entityId: string): boolean {
    return customerAllEntities
        .concat(contactsEntities)
        .map((entity) => entity.id)
        .includes(entityId)
}

// ENTREPRENEUR

export const entrepreneurPersonalEntities: SystemEntity[] = [
    clientLawAddress, clientLawName, clientOGRN, clientTaxId,
]

export const entrepreneurBankEntities: SystemEntity[] = [
    clientBIC, clientBankAccount, clientCorrAccount, clientBankName
]

export const entrepreneurAllEntities: SystemEntity[] = entrepreneurPersonalEntities
    .concat(entrepreneurBankEntities)


export function isEntrepreneurEntity(entityId: string): boolean {
    return entrepreneurAllEntities
        .concat(contactsEntities)
        .map((entity) => entity.id)
        .includes(entityId)
}

// ALL

export const allSystemEntities: SystemEntity[] = contractRelatedEntities
    .concat(accountRelatedEntities)

    .concat(customerPersonalEntities)
    .concat(customerBirthDateEntities)
    .concat(passportEntities)

    .concat(entrepreneurPersonalEntities)
    .concat(entrepreneurBankEntities)

    .concat(contactsEntities)

export const clearAfterCreateContractEntities: SystemEntity[] = customerPersonalEntities
    .concat(customerBirthDateEntities)
    .concat(passportEntities)

    .concat(entrepreneurPersonalEntities)
    .concat(entrepreneurBankEntities)

    .concat(contactsEntities)
    .concat(contractRelatedEntities)

export function bulkUpdateSystemEntities(contract: any, entities: SystemEntity[], path: string, value: any): any[] {
    return entities.map((systemEntity: SystemEntity) => {
        let contractEntity = contractSystemEntity(contract, systemEntity.id) ?? systemEntity
        _.set(contractEntity, path, value)
        return contractEntity
    })
}